import React, { useState, useEffect } from "react";
import "./inputStyle.scss";

import classnames from "classnames"

import {checkFontClassExistence} from "../../../utilities/regexCheckers"

import colorSchema from "../../../assets/kits/colors";
import { SearchNormalIcon } from "../Icons/Icons";

type TextType = "none" | "error" | "guide";

type intersectionProps = {
  title?: string;
  messageText?: string;
  textType?: TextType;
  textLength?: number;
  haveIcon?: boolean;
  containerClassName?: string;
  labelClassName?: string;
};

type CustomInputProps = intersectionProps &
  React.InputHTMLAttributes<HTMLInputElement>;

type CustomTextAreaProps = intersectionProps &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const Layout: React.FC<
  intersectionProps & {
    isInput?: boolean;
    id?: string;
    disabled?: boolean;
    value?: string | number | readonly string[];
    children?: JSX.Element | JSX.Element[];
  }
> = ({
  //text to show in title
  title = "",
  //error or warning text to show when error happened
  messageText = "",
  //limit character in input
  textLength = Infinity,
  isInput = false,
  haveIcon = false,
  textType = "none",
  containerClassName = "",
  labelClassName = "",
  disabled = false,
  id,
  value,
  children,
}) => {
  return (
    <div
      className={`inputs ${containerClassName} ${haveIcon ? "have-icon" : ""}`}
      style={{ width: "100%", position: "relative" }}
    >
      {title !== "" && (
        <label
          className={
            classnames(
              "label-style",
              {
                "disable-style": disabled,
                "desc-m": !checkFontClassExistence(labelClassName)
              },
              labelClassName
            )}
          htmlFor={id}
        >
          {title}
        </label>
      )}
      {/* icon */}
      {haveIcon && (
        <label
          className={`input-icon ${disabled && "disable-style"}`}
          style={{
            opacity: "0.4",
            pointerEvents: "inherit",
          }}
          htmlFor={id}
        >
          <SearchNormalIcon color={colorSchema.foreground_background_me} />
        </label>
      )}
      {children}
      {/* massage */}
      {(textType !== "none" || textLength !== Infinity) && (
        <div
          className="mr-t-4"
          style={{
            display: "flex",
            justifyContent:
              textType === "none" && disabled ? "end" : "space-between",
          }}
        >
          {textType !== "none" && !disabled && (
            <label
              className={`${
                textType === "guide" ? "warning-style" : "error-style"
              } massage desc-l`}
              style={{ width: textLength !== Infinity ? "95%" : "inherit" }}
            >
              {messageText}
            </label>
          )}
          {/* text length */}
          {textLength !== Infinity && isInput && (
            <label
              className="text-length desc-l"
              style={{
                width: textType !== "none" ? 0 : "inherit",
              }}
            >
              {(value + "").length}/{textLength}
            </label>
          )}
        </div>
      )}
    </div>
  );
};

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      //text to show in title
      title = "",
      //error or warning text to show when error happened
      messageText = "",
      //limit character in input
      textLength = Infinity,
      haveIcon = false,
      textType = "none",
      containerClassName = "",
      labelClassName = "",
      className,
      placeholder,
      style,
      value,
      onChange,
      onFocus,
      onBlur,
      ...otherProps
    },
    ref
  ) => {
    const [preview, setPreview] = useState<string>("");
    otherProps.id =
      otherProps.id || `${1000 + Math.floor(Math.random() * 1000)}`;

    useEffect(() => {
      setPreview(placeholder || "");
    }, [placeholder]);

    return (
      <Layout
        id={otherProps.id}
        title={title}
        messageText={messageText}
        textLength={textLength}
        haveIcon={haveIcon}
        textType={textType}
        containerClassName={containerClassName}
        labelClassName={labelClassName}
        isInput
      >
        {/* input */}
        <input
          ref={ref}
          className={`input-style txt-t2-l ${
            otherProps.disabled
              ? "disable-style"
              : textType === "error"
              ? "input-border-error"
              : textType === "guide"
              ? "input-border-warning"
              : ""
          } ${haveIcon ? "pd-r-40" : "pd-r-12"} ${className}`}
          placeholder={preview}
          value={value}
          onChange={(e) => {
            if (e.target.value.length <= textLength) {
              if (typeof onChange !== "undefined") {
                onChange(e);
              }
            }
          }}
          onBlur={(e) => {
            setPreview(placeholder || "");
            if (typeof onBlur !== "undefined") {
              onBlur(e);
            }
          }}
          onFocus={(e) => {
            setPreview("");
            if (typeof onFocus !== "undefined") {
              onFocus(e);
            }
          }}
          style={{
            width: "100%",
            ...style,
          }}
          {...otherProps}
        />
      </Layout>
    );
  }
);

const CustomTextArea = React.forwardRef<
  HTMLTextAreaElement,
  CustomTextAreaProps
>(
  (
    {
      //text to show in title
      title = "",
      //error or warning text to show when error happened
      messageText = "",
      //limit character in input
      textLength = Infinity,
      haveIcon = false,
      textType = "none",
      containerClassName = "",
      labelClassName = "",
      className,
      placeholder,
      style,
      value,
      onChange,
      onFocus,
      onBlur,
      ...otherProps
    },
    ref
  ) => {
    const [preview, setPreview] = useState<string>(placeholder || "");
    otherProps.id =
      otherProps.id || `${1000 + Math.floor(Math.random() * 1000)}`;

    return (
      <Layout
        id={otherProps.id}
        title={title}
        messageText={messageText}
        textLength={textLength}
        haveIcon={haveIcon}
        textType={textType}
        containerClassName={containerClassName}
        labelClassName={labelClassName}
      >
        {/* textarea */}
        <textarea
          ref={ref}
          className={`textarea-style txt-t2-l ${
            otherProps.disabled
              ? "disable-style"
              : textType === "error"
              ? "input-border-error"
              : textType === "guide"
              ? "input-border-warning"
              : ""
          } ${haveIcon ? "pd-r-40" : "pd-r-12"} ${className}`}
          placeholder={preview}
          value={value}
          onChange={(e) => {
            if (e.target.value.length <= textLength) {
              if (typeof onChange !== "undefined") {
                onChange(e);
              }
            }
          }}
          onBlur={(e) => {
            setPreview(placeholder || "");
            if (typeof onBlur !== "undefined") {
              onBlur(e);
            }
          }}
          onFocus={(e) => {
            setPreview("");
            if (typeof onFocus !== "undefined") {
              onFocus(e);
            }
          }}
          style={{
            width: "100%",
            resize: "none",
            ...style,
          }}
          {...otherProps}
        />
      </Layout>
    );
  }
);

export { CustomInput, CustomTextArea };

export type { CustomInputProps, CustomTextAreaProps };
